import React, { useState, useEffect, useMemo } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  Space,
  Input,
  Select,
  DatePicker,
  Table,
  Tag,
} from "antd";
import dayjs from "dayjs";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { GetInvoicesListApi } from "../InvoicesAPI";
import { FormattedMessage, useIntl } from "react-intl";
import StatusEnum from "features/Enums/StatusEnum";
import { getInvoicesListByProjectId } from "../InvoicesAPI";
import CreateQuote from "features/Dashboard/CreateQuote";
function InvoicesList(props) {
  const intl = useIntl();
  const { Search } = Input;
  const { Column } = Table;
  const dispatch = useDispatch();
  const companyId = localStorage.getItem("CompanyId");
  const { clientId, partnerId, workOrderId } = useParams();
  const { RangePicker } = DatePicker;
  const [dateRange, setDateRange] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState(0);
  const language = localStorage.getItem("selectedlanguage");
  const totalPages = useSelector((state) => state.invoices.totalPages);
  const invoicesList = useSelector((state) => state.invoices.invoicesList);
  const [invoiceStatusOptions, setInvoiceStatusOptions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };
  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };
  const input = {
    pageNo: currentPage,
    pageSize: 10,
    searchQuery: searchTerm,
    clientId: props?.clientId || null,
    partnerClientId: props?.partnerClientId || null,
    workOrderId: props?.workOrderId || null,
    dateRangeStart: dateRange?.[0]
      ? dayjs(dateRange[0]).format("YYYY-MM-DD")
      : null,
    dateRangeEnd: dateRange?.[1]
      ? dayjs(dateRange[1]).format("YYYY-MM-DD")
      : null,
    statusId: selectedStatus > 0 ? selectedStatus : null,
  };
  const getInvoiceStatusOptions = (lang) => [
    {
      value: 0,
      label: lang === "en-US" ? "All" : "Tout",
    },
    ...Object.entries(StatusEnum)
      .filter(([_, value]) => value.type === "InvoiceStatus")
      .sort(([, a], [, b]) => a.id - b.id)
      .map(([_, value]) => ({
        value: value.id,
        label: lang === "en-US" ? value.nameEn : value.nameFr,
      })),
  ];
  const getStatusColor = (status) => {
    switch (status) {
      case 29:
        return "default";
      case 30:
        return "blue";
      case 31:
        return "red";
      case 32:
        return "orange";
      case 33:
        return "green";
      case 34:
        return "volcano";
      default:
        return "default";
    }
  };
  useEffect(() => {
    const options = getInvoiceStatusOptions(language);
    setInvoiceStatusOptions(options);
  }, [language]);
  useEffect(() => {
    if (props?.projectId) {
      dispatch(getInvoicesListByProjectId(input, props?.projectId));
    } else {
      dispatch(GetInvoicesListApi(input));
    }
  }, [currentPage, searchTerm, selectedStatus, dateRange]);
  return (
    <>
      <>
        <Row align="bottom" gutter={[16, 16]}>
          <Col xs={24} sm={12} md={12} lg={8} xl={8}>
            <Search
              placeholder={intl.formatMessage({
                id: "Invoice.SearchInvoice",
              })}
              allowClear
              onSearch={handleSearch}
              defaultValue={searchTerm}
            />
          </Col>

          <Col xs={24} sm={12} md={12} lg={4} xl={6}>
            <RangePicker
              style={{ width: "100%" }}
              className="mobile-range-picker"
              popupClassName="mobile-range-popup"
              getPopupContainer={(trigger) => trigger.parentElement}
              value={dateRange}
              placeholder={[
                intl.formatMessage({
                  id: "Quote.StartDate",
                }),
                intl.formatMessage({
                  id: "Quote.EndDate",
                }),
              ]}
              onChange={(dates) => {
                setDateRange(dates);
                setCurrentPage(1);
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={12} lg={3} xl={3}>
            <Select
              value={selectedStatus}
              style={{ width: "100%" }}
              onChange={handleStatusChange}
              options={invoiceStatusOptions}
            />
          </Col>
          {props.CreateInvoiceVisible && (
            <Col xs={24} sm={12} md={12} lg={3} xl={3}>
              <Space>
                <Button type="primary" onClick={handleOpenModal}>
                  <FormattedMessage id="Invoice.GenerateInvoice" />
                </Button>
              </Space>
            </Col>
          )}
        </Row>
        <Table
          dataSource={invoicesList}
          bordered
          className="mt-10"
          scroll={{
            x: 1000,
          }}
          pagination={{
            current: currentPage,
            total: totalPages * 10,
            showSizeChanger: false,
            onChange: handlePageChange,
          }}
        >
          <Column
            title={<FormattedMessage id="Invoice.InvoiceNo" />}
            dataIndex="invoiceNumber"
            key="invoiceNumber"
            render={(invoiceNumber, record) => (
              <a
                href={
                  partnerId && clientId
                    ? `/${companyId}/${record.partnerClientId}/partner_client_details/${record.clientId}/client_details/${record.projectId}/projects/wo_details/${record.workOrderId}/viewinvoice/${record.id}`
                    : clientId
                    ? `/${companyId}/${record.clientId}/client_details/wo_details/${record.workOrderId}/viewinvoice/${record.id}`
                    : `/${companyId}/${record.partnerClientId}/partner_client_details/wo_details/${record.workOrderId}/viewinvoice/${record.id}`
                }
              >
                {invoiceNumber}
              </a>
            )}
          />
          <Column
            title={<FormattedMessage id="Invoice.InvoiceDate" />}
            dataIndex="invoiceDate"
            key="invoiceDate"
            render={(date) => (date ? dayjs(date).format("YYYY-MM-DD") : "")}
          />
          <Column
            title={
              <span>
                <FormattedMessage id="Clients.ClientName" /> /{" "}
                <FormattedMessage id="Dashboard.Clients.PartnerName" />
              </span>
            }
            dataIndex="clientName"
            key="clientName"
            render={(_, record) =>
              record.partnerClientName
                ? `${record.partnerClientName} (${record.clientName})`
                : record.clientName
            }
          />{" "}
          {/* For invoices and partners */}
          <Column
            title={<FormattedMessage id="Invoice.Amount" />}
            dataIndex="invoicingAmount"
            key="invoicingAmount"
          />
          {/* <Column
            title={<FormattedMessage id="Invoice.PendingAmount" />}
            dataIndex="amountOutstanding"
            key="amountOutstanding"
          />{" "} */}
          {/* For clients and WO */}
          <Column
            title={<FormattedMessage id="Invoice.Status" />}
            dataIndex="statusId"
            key="statusId"
            width={100}
            render={(statusId) => {
              const statusEntry = Object.values(StatusEnum).find(
                (status) => status.id === statusId
              );

              const statusLabel = statusEntry
                ? language === "en-US"
                  ? statusEntry.nameEn
                  : statusEntry.nameFr
                : statusId;
              return <Tag color={getStatusColor(statusId)}>{statusLabel}</Tag>;
            }}
          />
          <Column
            title={<FormattedMessage id="Common.Action" />}
            key="action"
            width={100}
            render={(_, record) => (
              <Space size="middle">
                <Link
                  to={
                    record.partnerId && record.clientId
                      ? `/${companyId}/${record.partnerClientId}/partner_client_details/${record.clientId}/client_details/wo_details/${record.workOrderId}/editinvoice/${record.id}`
                      : record.clientId
                      ? `/${companyId}/${record.clientId}/client_details/wo_details/${record.workOrderId}/editinvoice/${record.id}`
                      : `/${companyId}/${record.partnerClientId}/partner_client_details/wo_details/${record.workOrderId}/editinvoice/${record.id}`
                  }
                >
                  <Button
                    size="small"
                    disabled={record.statusId === StatusEnum.InvoiceSent.id}
                  >
                    <EditOutlined />
                  </Button>
                </Link>
                {/* <Button size="small" danger>
                  <DeleteOutlined />
                </Button> */}
              </Space>
            )}
          />
        </Table>
        <CreateQuote
          open={isModalOpen}
          onCancel={handleCloseModal}
          type="invoice"
        />
      </>
    </>
  );
}
export default InvoicesList;
